import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Settings2 } from "lucide-react";
import { api } from "@/lib/api";
import { Badge, btn, Spinner, STATUS_TONE, errMsg, blobErr, downloadAuthed, docsLabel } from "@/components/accounting/ui";
import { MonthPicker, SummaryCards } from "@/components/accounting/MonthPicker";
import UploadPanel from "@/components/accounting/UploadPanel";
import { DocFilters, DocRow } from "@/components/accounting/DocList";
import DocDialog from "@/components/accounting/DocDialog";
import MonthClose from "@/components/accounting/MonthClose";
import AccountingSettings from "@/components/accounting/AccountingSettings";

const EMPTY_FILTERS = { category: "", flag: "", uploaded: "", vehicle_id: "", search: "", all_periods: false };

export const AdminAccounting = ({ vehicles }) => {
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [period, setPeriod] = useState(null);
  const [ov, setOv] = useState(null);
  const [months, setMonths] = useState([]);
  const [docs, setDocs] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    api.get("/admin/accounting/meta")
      .then(({ data }) => { setMeta(data); setPeriod(data.current_period); })
      .catch((e) => setError(errMsg(e, "Buchhaltung konnte nicht geladen werden")));
  }, []);

  const periodLabel = useCallback((p) => (meta ? `${meta.months[Number(p.slice(5)) - 1]} ${p.slice(0, 4)}` : p), [meta]);

  const loadOverview = useCallback(async () => {
    if (!period) return;
    try {
      const [o, m] = await Promise.all([api.get("/admin/accounting/overview", { params: { period } }),
        api.get("/admin/accounting/months", { params: { year: period.slice(0, 4) } })]);
      setOv(o.data);
      setMonths(m.data);
    } catch (e) { toast.error(errMsg(e, "Übersicht konnte nicht geladen werden")); }
  }, [period]);

  const loadDocs = useCallback(async () => {
    if (!period) return;
    try {
      const { data } = await api.get("/admin/accounting/docs", { params: { period, ...filters } });
      setDocs(data.docs);
    } catch (e) { toast.error(errMsg(e, "Belege konnten nicht geladen werden")); }
  }, [period, filters]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { const t = setTimeout(loadDocs, filters.search ? 300 : 0); return () => clearTimeout(t); }, [loadDocs, filters.search]);

  const refresh = useCallback(() => Promise.all([loadOverview(), loadDocs()]), [loadOverview, loadDocs]);

  const download = async (d) => {
    try { await downloadAuthed(`/admin/accounting/docs/${d.id}/file?download=true`, d.original_filename); }
    catch (e) { toast.error(await blobErr(e, "Download fehlgeschlagen")); }
  };

  const remove = async (d) => {
    if (confirmId !== d.id) { setConfirmId(d.id); setTimeout(() => setConfirmId((c) => (c === d.id ? null : c)), 4000); return; }
    setConfirmId(null);
    try { await api.delete(`/admin/accounting/docs/${d.id}`); toast.success("Beleg in den Papierkorb verschoben"); refresh(); }
    catch (e) { toast.error(errMsg(e, "Löschen fehlgeschlagen")); }
  };

  const restore = async (d) => {
    try { await api.post(`/admin/accounting/docs/${d.id}/restore`); toast.success("Beleg wiederhergestellt"); refresh(); }
    catch (e) { toast.error(errMsg(e, "Wiederherstellen fehlgeschlagen")); }
  };

  if (error) return <p className="text-sm text-red-600 py-8" data-testid="acc-error">{error}</p>;
  if (!meta || !ov) return <div className="py-16 flex justify-center"><Spinner /></div>;

  return (
    <div className="space-y-6" data-testid="admin-accounting">
      <div className="flex flex-col md:flex-row md:items-start gap-4 justify-between">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display font-bold text-2xl text-ink">Buchhaltung & Belege</h2>
            <Badge tone={STATUS_TONE[ov.status]} testId="acc-month-status">{ov.label} · {ov.status_label}</Badge>
          </div>
          <p className="text-xs text-faint mt-1.5" data-testid="acc-month-summary">
            {docsLabel(ov.total)} · {ov.missing_count} mit fehlenden Angaben · {ov.duplicate_count} {ov.duplicate_count === 1 ? "mögliches Duplikat" : "mögliche Duplikate"}
          </p>
        </div>
        <button type="button" onClick={() => setSettingsOpen(true)} className={btn} data-testid="acc-settings-btn"><Settings2 size={13} /> Einstellungen</button>
      </div>

      <MonthPicker period={period} setPeriod={(p) => { setPeriod(p); setDocs(null); }} months={months} monthNames={meta.months} firstYear={meta.first_year} />
      <SummaryCards counts={ov.counts} category={filters.category} setCategory={(c) => setFilters((f) => ({ ...f, category: c }))} />

      <div className="grid lg:grid-cols-[1fr_420px] gap-6 items-start">
        <UploadPanel period={period} label={ov.label} meta={meta} onUploaded={refresh} />
        <MonthClose ov={ov} settings={meta.settings} email={meta.email} reload={refresh} onOpenSettings={() => setSettingsOpen(true)} />
      </div>

      <div className="space-y-3" data-testid="acc-doc-list">
        <DocFilters filters={filters} setFilters={setFilters} vehicles={vehicles} meta={meta} />
        {docs === null && <div className="py-10 flex justify-center"><Spinner /></div>}
        {docs?.length === 0 && <p className="text-sm text-faint py-8 text-center rounded-xl border border-dashed border-line" data-testid="acc-doc-empty">Keine Belege für diese Auswahl.</p>}
        {docs?.map((d) => (
          <DocRow key={d.id} d={d} meta={meta} periodLabel={periodLabel} showPeriod={filters.all_periods} trash={filters.flag === "trash"}
            confirmDelete={confirmId === d.id} onOpen={setEditing} onDownload={download} onDelete={remove} onRestore={restore} />
        ))}
      </div>

      <DocDialog doc={editing} meta={meta} vehicles={vehicles} periodLabel={periodLabel} onDownload={download}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />
      <AccountingSettings open={settingsOpen} settings={meta.settings} email={meta.email} onClose={() => setSettingsOpen(false)}
        onSaved={(data) => { const { email, ...settings } = data; setMeta((m) => ({ ...m, settings, email })); setSettingsOpen(false); }} />
    </div>
  );
};

export default AdminAccounting;
